import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./Opportunities.css";
import CreateOpportunities from "./CreateOpportunities";
import EditOpportunities from "./EditOpportunities";
import OpportunityDeleteDialog from "./OpportunityDeleteDialog";
import OpportunityHistoryDialog from "./OpportunityHistoryDialog";
import axiosInstance from "../../../Config/axiosInstance";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

const Opportunities = () => {
  const [rows, setRows] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditPopup, setIsEditPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);

  const [isHistoryDialogOpen, setHistoryDialogOpen] = useState(false);
  const [opportunityForHistory, setOpportunityForHistory] = useState(null);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPathFromUrl = (url) => {
    try {
      if (url.startsWith("/")) return url;
      const { pathname } = new URL(url);
      return pathname;
    } catch (error) {
      console.error("Eroare la extragerea URL-ului:", error);
      return url;
    }
  };

  const formatDateRO = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ro-RO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.error("Eroare la formatarea datei:", error);
      return dateString;
    }
  };

  const formatNumberRO = (value) => {
    if (value === null || value === undefined || value === "") return "";

    const raw = String(value).trim();
    const match = raw.match(/-?[\d.,\s]+/);
    if (!match) return raw;

    const numericPart = match[0].trim();
    let normalized = numericPart.replace(/\s/g, "");

    if (normalized.includes(",") && normalized.includes(".")) {
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else if (normalized.includes(",")) {
      normalized = normalized.replace(",", ".");
    }

    const n = Number(normalized);
    if (Number.isNaN(n)) return raw;

    const formatted = new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

    const suffix = raw.replace(match[0], "").trim();
    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  const sortRowsDesc = (items) => [...items].sort((a, b) => b.id - a.id);

  const loadOpportunities = useCallback(() => {
    axiosInstance
      .get("/opportunities/")
      .then((response) => {
        const dataWithFormattedDates = sortRowsDesc(response.data).map((item) => ({
          ...item,
          client_approach_date_display: formatDateRO(item.client_approach_date),
          info_update_date_display: formatDateRO(item.info_update_date),
        }));

        console.log(dataWithFormattedDates)
        setRows(dataWithFormattedDates);
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  useEffect(() => {
    const currentUrl = window.location.href;
    const url = getPathFromUrl(currentUrl);

    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasCreatePermission = permissions.some(
      (page) => getPathFromUrl(page.url) === url && page.can_create === true
    );
    setCanCreate(hasCreatePermission);

    const hasEditPermission = permissions.some(
      (page) => getPathFromUrl(page.url) === url && page.can_edit === true
    );
    setCanEdit(hasEditPermission);

    const hasDeletePermission = permissions.some(
      (page) => getPathFromUrl(page.url) === url && page.can_delete === true
    );
    setCanDelete(hasDeletePermission);

    loadOpportunities();
  }, [loadOpportunities]);

  const onAddOpportunities = (newOpportunity) => {
    axiosInstance
      .post("/opportunities/", newOpportunity)
      .then((response) => {
        const newRow = {
          ...response.data,
          client_approach_date_display: formatDateRO(
            response.data.client_approach_date
          ),
          info_update_date_display: formatDateRO(response.data.info_update_date),
        };

        setRows((prevRows) => sortRowsDesc([...prevRows, newRow]));
        setIsPopupOpen(false);
        setIsEditPopup(false);
        setSelectedRow(null);
      })
      .catch((error) => {
        console.error(
          "Error adding opportunity:",
          error.response ? error.response.data : error
        );
      });
  };

  const onEditOpportunities = (updatedOpportunity) => {
    axiosInstance
      .put(`/opportunities/${updatedOpportunity.id}/`, updatedOpportunity)
      .then((response) => {
        const updatedRow = {
          ...response.data,
          client_approach_date_display: formatDateRO(
            response.data.client_approach_date
          ),
          info_update_date_display: formatDateRO(response.data.info_update_date),
        };

        setRows((prevRows) =>
          sortRowsDesc(
            prevRows.map((row) =>
              row.id === updatedOpportunity.id ? updatedRow : row
            )
          )
        );

        setIsPopupOpen(false);
        setIsEditPopup(false);
        setSelectedRow(null);
      })
      .catch((error) => {
        console.error(
          "Error updating opportunity:",
          error.response ? error.response.data : error
        );
      });
  };

  const handleOpenCreate = () => {
    setSelectedRow(null);
    setIsEditPopup(false);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setIsEditPopup(false);
    setSelectedRow(null);
  };

  const toggleEditPopup = (row) => {
    setSelectedRow(row);
    setIsEditPopup(true);
    setIsPopupOpen(true);
  };

  const handleOpenDeleteDialog = (row) => {
    setOpportunityToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setOpportunityToDelete(null);
  };

  const handleOpportunityDeleted = async () => {
    handleCloseDeleteDialog();
    loadOpportunities();
  };

  const handleOpenHistoryDialog = (row) => {
    setOpportunityForHistory(row);
    setHistoryDialogOpen(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setOpportunityForHistory(null);
  };

  const columns = useMemo(() => {
    const cols = [
      {
        field: "code_opportunity",
        headerName: "Opportunity Code",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "title_opportunity",
        headerName: "Opportunity Title",
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: "client_name",
        headerName: "Client",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "project_name",
        headerName: "Project Name",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "project_code_label",
        headerName: "Project Code",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return <span>-</span>;
          }

          return <span>{params.value}</span>;
        },
      },
      {
        field: "subproject_code_label",
        headerName: "Sub-Project Code",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return <span>-</span>;
          }

          return <span>{params.value}</span>;
        },
      },
      {
        field: "department_label",
        headerName: "Department",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "responsible_person_label",
        headerName: "Responsible Person",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "issued_offer_no_current",
        headerName: "Issued Offer No.",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return <span>-</span>;
          }

          return <span>{params.value}</span>;
        },
      },
      {
        field: "issued_offer_value_current",
        headerName: "Issued Offer Value",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return <span>-</span>;
          }

          return <span>{formatNumberRO(params.value)}</span>;
        },
      },
      {
        field: "contract_value",
        headerName: "Contract Value",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return <span>-</span>;
          }

          return <span>{formatNumberRO(params.value)}</span>;
        },
      },
      {
        field: "first_contract_sign_date",
        headerName: "First Contract Sign Date",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          if(
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return <span>-</span>
          }
          return <span>{formatDateRO(params.value)}</span>
        }
      },
      {
        field: "remaining_to_invoice",
        headerName: "Remaining to invoice",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const contractValue = params.row?.contract_value;

          if (
            contractValue === null ||
            contractValue === undefined ||
            contractValue === ""
          ) {
            return <span>-</span>;
          }

          return <span>{formatNumberRO(params.value)}</span>;
        },
      },
      {
        field: "current_action_text",
        headerName: "Action",
        flex: 2.2,
        minWidth: 250,
      },
      {
        field: "status_offer",
        headerName: "Status",
        flex: 1,
        minWidth: 180,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const value = params.value;

          const statusMap = {
            // Offer statuses
            "Under client review": "open",
            "Contract signed": "approved",
            "On hold": "on-hold",
            "Offer closed": "declined",

            // Contract statuses
            "Active Contract": "active",
            "Suspended Contract": "suspended",
            "Completed Contract": "completed",
            "Cancelled Contract": "cancelled",
          };

          const className = statusMap[value];

          if (!value || !className) {
            return <span>-</span>;
          }

          return (
            <div className={`opportunities-status-badge ${className}`}>
              {value}
            </div>
          );
        },
        cellClassName: "status-cell",
      },
      {
        field: "client_approach_date_display",
        headerName: "Initial Client Approach Date",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "info_update_date_display",
        headerName: "Last Update",
        flex: 1,
        minWidth: 120,
      },

      {
        field: "chance_materialization",
        headerName: "Chances of Materialization",
        flex: 1.2,
        minWidth: 150,
        renderCell: (params) => {
          const value = params.value;

          if (value === null || value === undefined || value === "") {
            return <span>-</span>;
          }

          const numericValue = Number(value);
          let className = "";

          if (numericValue === 100) {
            className = "chance-materialization-green";
          } else if (numericValue >= 50) {
            className = "chance-materialization-blue";
          } else if (numericValue >= 25) {
            className = "chance-materialization-yellow";
          } else if (numericValue >= 0) {
            className = "chance-materialization-red";
          }

          return <div className={className}>{numericValue}%</div>;
        },
      },
      {
        field: "comments",
        headerName: "Comments",
        flex: 2,
        minWidth: 300,
      },
    ];

    cols.push({
      field: "actions",
      headerName: "Actions",
      width: canEdit || canDelete ? 170 : 80,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() => handleOpenHistoryDialog(params.row)}
            sx={{ color: "#6a1b9a" }}
            title="View action history"
          >
            <VisibilityIcon />
          </IconButton>

          {canEdit && (
            <IconButton
              onClick={() => toggleEditPopup(params.row)}
              sx={{ color: "#1976d2" }}
              title="Edit opportunity"
            >
              <EditIcon />
            </IconButton>
          )}

          {canDelete && (
            <IconButton
              onClick={() => handleOpenDeleteDialog(params.row)}
              sx={{ color: "#d32f2f" }}
              title="Delete opportunity"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
    });

    return cols;
  }, [canEdit, canDelete]);

  return (
    <div className="opportunities_page">
      <div
        className={`opportunities-page opportunities-content-container ${isPopupOpen ? "no-scroll" : ""
          }`}
      >
        <div className="opportunities-header-container">
          <h2>OPPORTUNITIES</h2>

          {canCreate && (
            <Button
              variant="contained"
              onClick={handleOpenCreate}
              startIcon={<AddIcon />}
            >
              {windowWidth < 400 ? "Add" : "Add Opportunity"}
            </Button>
          )}
        </div>

        <Box className="opportunities-box-subcontainer scrollable-box opportunities-datagrid">
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            getRowHeight={() => "auto"}
            scrollbarX={true}
            initialState={{
              sorting: {
                sortModel: [{ field: "id", sort: "desc" }],
              },
              pagination: {
                paginationModel: { pageSize: 12 },
              },
            }}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 12, 20, 25]}
            sx={{
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: "1.4",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-columnHeaders": {
                whiteSpace: "normal",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .Mui-selected:hover": {
                backgroundColor: "transparent !important",
              },
            }}
          />
        </Box>

        {isPopupOpen && isEditPopup ? (
          <EditOpportunities
            open={isPopupOpen}
            toggleAddPopup={handleClosePopup}
            onEditOpportunities={onEditOpportunities}
            selectedRow={selectedRow}
          />
        ) : isPopupOpen ? (
          <CreateOpportunities
            open={isPopupOpen}
            toggleAddPopup={handleClosePopup}
            onAddOpportunities={onAddOpportunities}
          />
        ) : null}

        <OpportunityDeleteDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          opportunity={opportunityToDelete}
          onDeleted={handleOpportunityDeleted}
        />

        <OpportunityHistoryDialog
          open={isHistoryDialogOpen}
          onClose={handleCloseHistoryDialog}
          opportunity={opportunityForHistory}
        />
      </div>
    </div>
  );
};

export default Opportunities;